import React, { useState } from "react";
import { Link } from "react-router-dom";

import { Logo } from "../../../shared/components/Logo";
import { forgotPassword, confirmForgotPassword } from "../client";

export function ForgotPasswordPage(): JSX.Element {
  const [step, setStep] = useState<"request" | "confirm">("request");
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  async function handleRequestCode(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    setError(null);

    if (!email.trim()) {
      setError("Please enter your email address.");
      return;
    }

    setSubmitting(true);
    try {
      await forgotPassword(email.trim());
      setStep("confirm");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to send reset code.");
    } finally {
      setSubmitting(false);
    }
  }

  async function handleConfirmReset(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    setError(null);

    if (!code.trim() || !newPassword) {
      setError("Please enter both the verification code and your new password.");
      return;
    }

    setSubmitting(true);
    try {
      await confirmForgotPassword(email.trim(), code.trim(), newPassword);
      setSuccess(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to reset password.");
    } finally {
      setSubmitting(false);
    }
  }

  if (success) {
    return (
      <main className="auth-page auth-page-signin" aria-labelledby="reset-title">
        <section className="auth-card">
          <div className="auth-logo-area">
            <Logo size={40} />
            <span className="auth-logo-text">Q<span className="auth-logo-accent">picks</span></span>
          </div>
          <h1 id="reset-title" className="auth-title">Password reset</h1>
          <p className="auth-subtitle">
            Your password has been updated successfully.
          </p>
          <Link to="/signin" className="auth-primary-button auth-primary-button-link">
            Back to Sign In
          </Link>
        </section>
      </main>
    );
  }

  return (
    <main className="auth-page auth-page-signin" aria-labelledby="forgot-title">
      <section className="auth-card">
        <div className="auth-logo-area">
          <Logo size={40} />
          <span className="auth-logo-text">Q<span className="auth-logo-accent">picks</span></span>
        </div>

        <h1 id="forgot-title" className="auth-title">
          {step === "request" ? "Reset password" : "Enter verification code"}
        </h1>
        <p className="auth-subtitle">
          {step === "request"
            ? "Enter your email address and we'll send you a verification code."
            : `We sent a code to ${email}. Enter it below with your new password.`}
        </p>

        {step === "request" ? (
          <form onSubmit={(e) => void handleRequestCode(e)} noValidate>
            <label htmlFor="forgot-email" className="auth-label">Email</label>
            <input
              id="forgot-email"
              className="auth-input"
              type="email"
              placeholder="you@example.com"
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={submitting}
            />
            <button type="submit" className="auth-primary-button" disabled={submitting}>
              {submitting ? "Sending..." : "Send Reset Code"}
            </button>
          </form>
        ) : (
          <form onSubmit={(e) => void handleConfirmReset(e)} noValidate>
            <label htmlFor="forgot-code" className="auth-label">Verification Code</label>
            <input
              id="forgot-code"
              className="auth-input"
              type="text"
              placeholder="123456"
              autoComplete="one-time-code"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              disabled={submitting}
            />
            <label htmlFor="forgot-newpw" className="auth-label">New Password</label>
            <input
              id="forgot-newpw"
              className="auth-input"
              type="password"
              placeholder="••••••••"
              autoComplete="new-password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              disabled={submitting}
            />
            <button type="submit" className="auth-primary-button" disabled={submitting}>
              {submitting ? "Resetting..." : "Reset Password"}
            </button>
          </form>
        )}

        <Link to="/signin" className="auth-link">Back to Sign In</Link>

        {error && (
          <p role="alert" className="auth-error">{error}</p>
        )}
      </section>
    </main>
  );
}
